"use client";

/**
 * NewSinceLine: "34 new since 24 Sep" for visitors who come back to the library
 * after a day or more (audit X9 / LOOP-2). Renders nothing for first visits,
 * for same-day returns, and when nothing new was added. The last visit is
 * kept in this browser only.
 */

import { useEffect, useState } from "react";
import Link from "next/link";

import {
  formatNewSince,
  readSessionResult,
  takeReturningVisit,
  writeSessionResult,
  type NewSinceResult,
} from "@/lib/new-since";
import { trackNewSinceLineClick } from "@/lib/tracking";

export function NewSinceLine({ className }: { className?: string }) {
  const [result, setResult] = useState<NewSinceResult | null>(null);

  useEffect(() => {
    const remembered = readSessionResult();
    if (remembered) {
      // eslint-disable-next-line react-hooks/set-state-in-effect -- hydration-safe reveal: SSR and first client render agree on null
      setResult(remembered);
      return;
    }
    const day = takeReturningVisit(Date.now());
    if (!day) return;

    const controller = new AbortController();
    fetch(`/api/sounds/new-since?since=${day}`, { signal: controller.signal })
      .then((res) => (res.ok ? (res.json() as Promise<NewSinceResult>) : null))
      .then((data) => {
        if (!data || typeof data.count !== "number" || data.count <= 0) return;
        const next = { since: data.since, count: data.count };
        writeSessionResult(next);
        setResult(next);
      })
      .catch(() => {
        // The line is a courtesy: no count, no line.
      });
    return () => controller.abort();
  }, []);

  if (!result) return null;

  return (
    <p
      className={`m-0 border-y border-[color:var(--tls-border-subtle)] py-2.5 text-sm text-[color:var(--tls-fg-muted)] ${className ?? ""}`}
    >
      <strong style={{ color: "var(--tls-fg-strong)", fontWeight: 600 }}>{formatNewSince(result)}.</strong>{" "}
      <Link
        href="/sounds?sort=created_at_desc"
        onClick={() => trackNewSinceLineClick({ result_count: result.count })}
        className="tlsr-focus underline underline-offset-2"
        style={{ color: "var(--tls-fg-strong)" }}
      >
        Newest first
      </Link>
    </p>
  );
}
